import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, Upload, Square, Cpu, Target, FileSpreadsheet, 
  Download, FolderOpen, Settings, HelpCircle, ChevronRight, 
  ArrowLeft, Printer, CheckCircle2, Zap, MousePointer, Info, 
  FileText, Edit2, Move, Trash2, Layers, Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

// Print styles - inject into head
const PrintStyles = () => (
  <style>{`
    @media print {
      /* Show ALL sections during print */
      .guide-section {
        display: block !important;
      }
      
      /* Hide navigation elements */
      .print-hide {
        display: none !important;
      }
      
      /* Page breaks */
      .guide-section {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      /* Reset backgrounds for print */
      body {
        background: white !important;
      }
      
      /* Ensure proper spacing */
      .guide-content {
        padding: 0 !important;
        max-width: 100% !important;
      }
      
      /* Hide DenAIs chatbot */
      [data-testid="ai-chat-button"],
      [data-testid="ai-chat-window"] {
        display: none !important;
      }
    }
  `}</style>
);

// Helper component for step cards
const StepCard = ({ number, title, tip, children, t }) => (
  <div className="bg-slate-50 rounded-lg p-4 print:break-inside-avoid">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-slate-800">{title}</h4>
        <div className="text-sm text-slate-600 mt-1 space-y-2">
          {children}
        </div>
        {tip && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <strong>{t('guide_suggestion')}</strong> {tip}
          </div>
        )}
      </div>
    </div>
  </div>
);

// Helper component for info boxes
const InfoBox = ({ type = 'info', children }) => {
  const colors = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-800'
  };
  
  return (
    <div className={`p-4 rounded-lg border ${colors[type]} text-sm print:break-inside-avoid`}>
      {children}
    </div>
  );
};

const GuidePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', icon: BookOpen, label: t('guide_intro') },
    { id: 'upload', icon: Upload, label: t('guide_upload') },
    { id: 'views', icon: Square, label: t('guide_views') },
    { id: 'ai-detection', icon: Cpu, label: t('guide_ai_detection') },
    { id: 'markers', icon: Target, label: t('guide_markers') },
    { id: 'cmm', icon: FileSpreadsheet, label: t('guide_cmm') },
    { id: 'export', icon: Download, label: t('guide_export') },
    { id: 'smartrevision', icon: Layers, label: t('guide_smartrevision') },
    { id: 'projects', icon: FolderOpen, label: t('guide_projects') },
    { id: 'settings', icon: Settings, label: t('guide_settings') },
    { id: 'faq', icon: HelpCircle, label: t('guide_faq') },
  ];

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <PrintStyles />
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 print:static print:border-0 print-hide">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="print:hidden"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back')}
            </Button>
            <h1 className="text-xl font-bold text-slate-800 print:text-2xl print:mb-4">{t('guide_title')}</h1>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.print()}
            >
              <Printer className="w-4 h-4 mr-2" />
              {t('print_save_pdf')}
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
            >
              {t('go_to_dashboard')}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 print:py-4 guide-content">
        <div className="flex gap-8 print:block">
          {/* Sidebar Navigation */}
          <aside className="w-64 flex-shrink-0 print:hidden print-hide">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">{t('table_of_contents')}</h2>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{section.label}</span>
                    {activeSection === section.id && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content - show all sections when printing */}
          <main className="flex-1 min-w-0 space-y-8 print:space-y-4">
            {/* Introduzione */}
            <div className={`guide-section ${activeSection === 'intro' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    {t('guide_welcome_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_welcome_desc')}</p>
                  
                  <div className="grid md:grid-cols-3 gap-4 my-6">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg text-center">
                      <Upload className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">1. {t('guide_step_upload')}</h4>
                      <p className="text-xs text-slate-600 mt-1">{t('guide_step_upload_desc')}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg text-center">
                      <Cpu className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">2. {t('guide_step_detect')}</h4>
                      <p className="text-xs text-slate-600 mt-1">{t('guide_step_detect_desc')}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg text-center">
                      <Download className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">3. {t('guide_step_export')}</h4>
                      <p className="text-xs text-slate-600 mt-1">{t('guide_step_export_desc')}</p>
                    </div>
                  </div>

                  <InfoBox type="info">
                    <strong>{t('guide_suggestion')}</strong> {t('guide_tip_vector')}
                  </InfoBox>

                  <h3 className="font-semibold text-slate-800 mt-6 mb-3">{t('guide_features_title')}</h3>
                  <ul className="space-y-2">
                    {[
                      t('guide_feature_1'),
                      t('guide_feature_2'),
                      t('guide_feature_3'),
                      t('guide_feature_4'),
                      t('guide_feature_5'),
                      t('guide_feature_6')
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* 1. Caricamento Disegno */}
            <div className={`guide-section ${activeSection === 'upload' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-600" />
                    {t('guide_upload_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_upload_intro')}</p>

                  <StepCard number="1" title={t('guide_upload_step1_title')} t={t}>
                    <p>{t('guide_upload_step1_desc1')}</p>
                    <p>{t('guide_upload_step1_desc2')}</p>
                  </StepCard>

                  <StepCard number="2" title={t('guide_upload_step2_title')} t={t}>
                    <p>{t('guide_upload_step2_desc1')}</p>
                    <p>{t('guide_upload_step2_desc2')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong>{t('guide_upload_step2_opt1')}</strong></li>
                      <li><strong>{t('guide_upload_step2_opt2')}</strong></li>
                    </ul>
                    <p className="mt-2">{t('guide_upload_step2_formats')}</p>
                  </StepCard>

                  <StepCard 
                    number="3" 
                    title={t('guide_upload_step3_title')}
                    tip={t('guide_upload_step3_tip')}
                    t={t}
                  >
                    <p>{t('guide_upload_step3_desc1')}</p>
                    <p>{t('guide_upload_step3_desc2')}</p>
                  </StepCard>

                  <InfoBox type="success">
                    <strong>{t('guide_vector_vs_raster').split(':')[0]}:</strong> {t('guide_vector_vs_raster').split(':').slice(1).join(':')}
                  </InfoBox>
                </CardContent>
              </Card>
            </div>

            {/* 2. Definire le Viste */}
            <div className={`guide-section ${activeSection === 'views' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Square className="w-5 h-5 text-blue-600" />
                    {t('guide_views_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_views_intro')}</p>

                  <StepCard number="1" title={t('guide_views_step1_title')} t={t}>
                    <p>{t('guide_views_step1_desc1')}</p>
                    <p>{t('guide_views_step1_desc2')}</p>
                  </StepCard>

                  <StepCard 
                    number="2" 
                    title={t('guide_views_step2_title')}
                    tip={t('guide_views_step2_tip')}
                    t={t}
                  >
                    <p>{t('guide_views_step2_desc1')}</p>
                    <p>{t('guide_views_step2_desc2')}</p>
                  </StepCard>

                  <StepCard number="3" title={t('guide_views_step3_title')} t={t}>
                    <p>{t('guide_views_step3_desc1')}</p>
                    <p>{t('guide_views_step3_desc2')}</p>
                  </StepCard>

                  <InfoBox type="warning">
                    {t('guide_views_credits_warning')}
                  </InfoBox>
                </CardContent>
              </Card>
            </div>

            {/* 3. Rilevamento AI */}
            <div className={`guide-section ${activeSection === 'ai-detection' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-blue-600" />
                    {t('guide_ai_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_ai_intro')}</p>

                  <h3 className="font-semibold text-slate-800 mt-4 mb-3">{t('guide_ai_methods_title')}</h3>

                  <div className="space-y-4">
                    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        {t('guide_ai_vector_title')}
                      </h4>
                      <p className="text-sm text-green-700 mt-2">
                        {t('guide_ai_vector_desc')}
                        <br/><strong>{t('guide_ai_vector_cost')}</strong>
                      </p>
                      <p className="text-sm text-green-700 mt-2">
                        <strong>{t('guide_ai_vector_how')}</strong>
                      </p>
                    </div>

                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                        <Square className="w-4 h-4" />
                        {t('guide_ai_views_title')}
                      </h4>
                      <p className="text-sm text-blue-700 mt-2">
                        {t('guide_ai_views_desc')}
                        <br/><strong>{t('guide_ai_views_cost')}</strong>
                      </p>
                      <p className="text-sm text-blue-700 mt-2">
                        <strong>{t('guide_ai_views_how')}</strong>
                      </p>
                    </div>

                    <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                        <MousePointer className="w-4 h-4" />
                        {t('guide_ai_manual_title')}
                      </h4>
                      <p className="text-sm text-purple-700 mt-2">
                        {t('guide_ai_manual_desc')}
                        <br/><strong>{t('guide_ai_manual_cost')}</strong>
                      </p>
                      <p className="text-sm text-purple-700 mt-2">
                        <strong>{t('guide_ai_manual_how')}</strong>
                      </p>
                    </div>
                  </div>

                  <StepCard 
                    number="4" 
                    title={t('guide_ai_review_title')}
                    tip={t('guide_ai_review_tip')}
                    t={t}
                  >
                    <p>{t('guide_ai_review_desc1')}</p>
                    <p>{t('guide_ai_review_desc2')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong>{t('guide_ai_review_accept')}</strong></li>
                      <li><strong>{t('guide_ai_review_modify')}</strong></li>
                      <li><strong>{t('guide_ai_review_reject')}</strong></li>
                    </ul>
                  </StepCard>
                </CardContent>
              </Card>
            </div>

            {/* 4. Gestione Markers */}
            <div className={`guide-section ${activeSection === 'markers' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    {t('guide_markers_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_markers_intro')}</p>

                  <StepCard number="1" title={t('guide_markers_step1_title')} t={t}>
                    <p>{t('guide_markers_step1_desc1')}</p>
                    <p>{t('guide_markers_step1_desc2')}</p>
                  </StepCard>

                  <StepCard number="2" title={t('guide_markers_step2_title')} t={t}>
                    <p>{t('guide_markers_step2_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong>{t('guide_markers_field_number')}</strong></li>
                      <li><strong>{t('guide_markers_field_nominal')}</strong></li>
                      <li><strong>{t('guide_markers_field_tolerance')}</strong></li>
                      <li><strong>{t('guide_markers_field_description')}</strong></li>
                    </ul>
                  </StepCard>

                  <StepCard 
                    number="3" 
                    title={t('guide_markers_step3_title')}
                    tip={t('guide_markers_step3_tip')}
                    t={t}
                  >
                    <p>{t('guide_markers_step3_desc1')}</p>
                    <p>{t('guide_markers_step3_desc2')}</p>
                  </StepCard>

                  <StepCard number="4" title={t('guide_markers_step4_title')} t={t}>
                    <p>{t('guide_markers_step4_desc1')}</p>
                    <p>{t('guide_markers_step4_desc2')}</p>
                  </StepCard>

                  <InfoBox type="info">
                    {t('guide_markers_numbering_info')}
                  </InfoBox>
                </CardContent>
              </Card>
            </div>

            {/* 5. Import CMM */}
            <div className={`guide-section ${activeSection === 'cmm' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                    {t('guide_cmm_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_cmm_intro')}</p>

                  <InfoBox type="info">
                    {t('guide_cmm_new_feature')}
                  </InfoBox>

                  <StepCard number="1" title={t('guide_cmm_step1_title')} t={t}>
                    <p>{t('guide_cmm_step1_desc')}</p>
                    <div className="bg-slate-50 rounded p-3 mt-2 font-mono text-sm">
                      <p className="text-green-700">✓ "Diametro D=4 <strong>SB131</strong> 4.0120"</p>
                      <p className="text-green-700">✓ "Posizione X <strong>SB45</strong> 10.5"</p>
                      <p className="text-green-700">✓ "Centro foro <strong>sb7</strong> 25.00"</p>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">{t('guide_cmm_step1_format')}</p>
                  </StepCard>

                  <StepCard number="2" title={t('guide_cmm_step2_title')} t={t}>
                    <p>{t('guide_cmm_step2_desc1')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li>{t('guide_cmm_step2_desc2')}</li>
                      <li>{t('guide_cmm_step2_desc3')}</li>
                      <li>{t('guide_cmm_step2_desc4')}</li>
                    </ul>
                    <p className="mt-2 text-sm text-slate-500">{t('guide_cmm_step2_note')}</p>
                  </StepCard>

                  <StepCard 
                    number="3" 
                    title={t('guide_cmm_step3_title')}
                    tip={t('guide_cmm_step3_tip')}
                    t={t}
                  >
                    <p>{t('guide_cmm_step3_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li>{t('guide_cmm_step3_process1')}</li>
                      <li>{t('guide_cmm_step3_process2')}</li>
                      <li>{t('guide_cmm_step3_process3')}</li>
                    </ul>
                  </StepCard>

                  <StepCard number="4" title={t('guide_cmm_step4_title')} t={t}>
                    <p>{t('guide_cmm_step4_desc')}</p>
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                      <p className="font-semibold text-amber-800 mb-2">{t('cmm_duplicate_title', { id: '45' })}</p>
                      <div className="space-y-1 text-sm text-amber-700">
                        <p>○ <strong>15.02</strong> mm (Report_CMM_Lot1.pdf)</p>
                        <p>○ <strong>15.05</strong> mm (Report_CMM_Lot2.pdf)</p>
                        <p>○ <strong>14.98</strong> mm (Report_CMM_Lot3.pdf)</p>
                      </div>
                    </div>
                    <p className="mt-2">{t('guide_cmm_step4_action')}</p>
                  </StepCard>

                  <StepCard number="5" title={t('guide_cmm_step5_title')} t={t}>
                    <p>{t('guide_cmm_step5_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li>{t('guide_cmm_step5_check')}</li>
                      <li>{t('guide_cmm_step5_apply')}</li>
                    </ul>
                    <p className="mt-2">{t('guide_cmm_step5_result')}</p>
                  </StepCard>

                  <InfoBox type="success">
                    {t('guide_cmm_oknok_info')}
                  </InfoBox>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">{t('guide_cmm_summary_title')}</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• {t('guide_cmm_summary_1')}</li>
                      <li>• {t('guide_cmm_summary_2')}</li>
                      <li>• {t('guide_cmm_summary_3')}</li>
                      <li>• {t('guide_cmm_summary_4')}</li>
                      <li>• {t('guide_cmm_summary_5')}</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 6. Esportazione */}
            <div className={`guide-section ${activeSection === 'export' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    {t('guide_export_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_export_intro')}</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-red-500" />
                        {t('guide_export_pdf_title')}
                      </h4>
                      <p className="text-sm text-slate-600">{t('guide_export_pdf_desc')}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        <strong>{t('guide_export_pdf_how')}</strong>
                      </p>
                    </div>

                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                        <FileSpreadsheet className="w-4 h-4 text-green-500" />
                        {t('guide_export_excel_title')}
                      </h4>
                      <p className="text-sm text-slate-600">{t('guide_export_excel_desc')}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        <strong>{t('guide_export_excel_how')}</strong>
                      </p>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-800 mt-6 mb-3">{t('guide_export_excel_features_title')}</h3>
                  <ul className="space-y-2">
                    {[
                      t('guide_export_excel_feature_1'),
                      t('guide_export_excel_feature_2'),
                      t('guide_export_excel_feature_3'),
                      t('guide_export_excel_feature_4'),
                      t('guide_export_excel_feature_5'),
                      t('guide_export_excel_feature_6')
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <InfoBox type="info">
                    {t('guide_export_logo_info')}
                  </InfoBox>
                </CardContent>
              </Card>
            </div>

            {/* 7. SmartRevision */}
            <div className={`guide-section ${activeSection === 'smartrevision' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-purple-600" />
                    {t('guide_smartrevision_title')}
                    <span className="text-sm font-normal text-slate-500">{t('guide_smartrevision_subtitle')}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_smartrevision_intro')}</p>
                  
                  <InfoBox type="info">
                    {t('guide_smartrevision_nav_info')}
                  </InfoBox>

                  <InfoBox type="warning">
                    {t('guide_smartrevision_credits_warning')}
                  </InfoBox>

                  <StepCard number="1" title={t('guide_smartrevision_step1_title')} t={t}>
                    <p>{t('guide_smartrevision_step1_desc1')}</p>
                    <p>{t('guide_smartrevision_step1_desc2')}</p>
                  </StepCard>

                  <StepCard number="2" title={t('guide_smartrevision_step2_title')} t={t}>
                    <p>{t('guide_smartrevision_step2_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong>{t('guide_smartrevision_step2_old')}</strong></li>
                      <li><strong>{t('guide_smartrevision_step2_new')}</strong></li>
                    </ul>
                    <p className="mt-2">{t('guide_smartrevision_step2_vector')}</p>
                    <p className="mt-2 text-sm text-slate-500">{t('guide_smartrevision_step2_rename')}</p>
                  </StepCard>

                  <StepCard 
                    number="3" 
                    title={t('guide_smartrevision_step3_title')}
                    tip={t('guide_smartrevision_step3_tip')}
                    t={t}
                  >
                    <p>{t('guide_smartrevision_step3_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li>{t('guide_smartrevision_step3_process1')}</li>
                      <li>{t('guide_smartrevision_step3_process2')}</li>
                      <li>{t('guide_smartrevision_step3_process3')}</li>
                    </ul>
                  </StepCard>

                  <StepCard number="4" title={t('guide_smartrevision_step4_title')} t={t}>
                    <p>{t('guide_smartrevision_step4_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong className="text-red-600">{t('guide_smartrevision_color_red')}</strong></li>
                      <li><strong className="text-green-600">{t('guide_smartrevision_color_green')}</strong></li>
                      <li><strong className="text-yellow-600">{t('guide_smartrevision_color_yellow')}</strong></li>
                    </ul>
                    <p className="mt-2 text-sm text-slate-500">{t('guide_smartrevision_overlay_note')}</p>
                  </StepCard>

                  <StepCard 
                    number="4.1" 
                    title={t('guide_smartrevision_step4_1_title')}
                    tip={t('guide_smartrevision_step4_1_tip')}
                    t={t}
                  >
                    <p>{t('guide_smartrevision_step4_1_desc')}</p>
                    <p className="mt-2"><strong>{t('guide_smartrevision_editor_features')}</strong></p>
                    <ul className="list-disc ml-4 mt-2 space-y-1 text-sm">
                      <li><strong>{t('guide_smartrevision_editor_slider')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_zoom')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_pan')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_select')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_move')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_resize')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_delete')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_add')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_note')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_undo')}</strong></li>
                      <li><strong>{t('guide_smartrevision_editor_save')}</strong></li>
                    </ul>
                    <p className="mt-2 text-sm text-slate-500">{t('guide_smartrevision_editor_important')}</p>
                    <p className="mt-2 text-sm text-blue-600">{t('guide_smartrevision_user_circles')}</p>
                  </StepCard>

                  <StepCard number="5" title={t('guide_smartrevision_step5_title')} t={t}>
                    <p>{t('guide_smartrevision_step5_desc')}</p>
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li><strong>{t('guide_smartrevision_step5_page1')}</strong></li>
                      <li><strong>{t('guide_smartrevision_step5_page2')}</strong></li>
                    </ul>
                  </StepCard>

                  <InfoBox type="success">
                    {t('guide_smartrevision_format_preserved')}
                  </InfoBox>

                  <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">{t('guide_smartrevision_use_cases_title')}</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• {t('guide_smartrevision_use_case_1')}</li>
                      <li>• {t('guide_smartrevision_use_case_2')}</li>
                      <li>• {t('guide_smartrevision_use_case_3')}</li>
                      <li>• {t('guide_smartrevision_use_case_4')}</li>
                      <li>• {t('guide_smartrevision_use_case_5')}</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-2">{t('guide_smartrevision_advanced_title')}</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• {t('guide_smartrevision_advanced_1')}</li>
                      <li>• {t('guide_smartrevision_advanced_2')}</li>
                      <li>• {t('guide_smartrevision_advanced_3')}</li>
                      <li>• {t('guide_smartrevision_advanced_4')}</li>
                      <li>• {t('guide_smartrevision_advanced_5')}</li>
                      <li>• {t('guide_smartrevision_advanced_6')}</li>
                      <li>• {t('guide_smartrevision_advanced_7')}</li>
                      <li>• {t('guide_smartrevision_advanced_8')}</li>
                      <li>• {t('guide_smartrevision_advanced_9')}</li>
                      <li>• {t('guide_smartrevision_advanced_10')}</li>
                      <li>• {t('guide_smartrevision_advanced_11')}</li>
                    </ul>
                  </div>
                  
                  {/* Guided View Comparison Section */}
                  <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded">{t('guide_smartrevision_guided_title')}</span>
                      {t('guide_smartrevision_guided_subtitle')}
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">{t('guide_smartrevision_guided_desc')}</p>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-1">{t('guide_smartrevision_guided_when_title')}</p>
                        <ul className="text-sm text-slate-600 space-y-1 ml-4">
                          <li>• {t('guide_smartrevision_guided_when_1')}</li>
                          <li>• {t('guide_smartrevision_guided_when_2')}</li>
                          <li>• {t('guide_smartrevision_guided_when_3')}</li>
                          <li>• {t('guide_smartrevision_guided_when_4')}</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-1">{t('guide_smartrevision_guided_how_title')}</p>
                        <ol className="text-sm text-slate-600 space-y-1 ml-4 list-decimal">
                          <li>{t('guide_smartrevision_guided_how_1')}</li>
                          <li>{t('guide_smartrevision_guided_how_2')}</li>
                          <li>{t('guide_smartrevision_guided_how_3')}</li>
                          <li>{t('guide_smartrevision_guided_how_4')}</li>
                          <li>{t('guide_smartrevision_guided_how_5')}</li>
                        </ol>
                      </div>
                      
                      <div className="flex items-center gap-2 p-2 bg-amber-100 rounded text-sm text-amber-800">
                        {t('guide_smartrevision_guided_cost')}
                      </div>
                    </div>
                  </div>
                  
                  <InfoBox type="info">
                    {t('guide_smartrevision_popup_warning')}
                  </InfoBox>
                </CardContent>
              </Card>
            </div>

            {/* Gestione Progetti */}
            <div className={`guide-section ${activeSection === 'projects' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                    {t('guide_projects_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{t('guide_projects_intro')}</p>

                  <h3 className="font-semibold text-slate-800 mt-4 mb-3">{t('guide_projects_folders_title')}</h3>
                  <StepCard number="1" title={t('guide_projects_folder_step1_title')} t={t}>
                    <p>{t('guide_projects_folder_step1_desc1')}</p>
                    <p>{t('guide_projects_folder_step1_desc2')}</p>
                  </StepCard>

                  <StepCard number="2" title={t('guide_projects_folder_step2_title')} t={t}>
                    <p>{t('guide_projects_folder_step2_desc1')}</p>
                    <p>{t('guide_projects_folder_step2_desc2')}</p>
                  </StepCard>

                  <h3 className="font-semibold text-slate-800 mt-6 mb-3">{t('guide_projects_actions_title')}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Edit2 className="w-4 h-4 text-blue-500" />
                        <span className="font-medium text-sm">{t('guide_projects_action_rename')}</span>
                      </div>
                      <p className="text-xs text-slate-600">{t('guide_projects_action_rename_how')}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Move className="w-4 h-4 text-amber-500" />
                        <span className="font-medium text-sm">{t('guide_projects_action_move')}</span>
                      </div>
                      <p className="text-xs text-slate-600">{t('guide_projects_action_move_how')}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Trash2 className="w-4 h-4 text-red-500" />
                        <span className="font-medium text-sm">{t('guide_projects_action_delete')}</span>
                      </div>
                      <p className="text-xs text-slate-600">{t('guide_projects_action_delete_how')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Impostazioni */}
            <div className={`guide-section ${activeSection === 'settings' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    {t('guide_settings_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <h3 className="font-semibold text-slate-800 mb-3">{t('guide_settings_logo_title')}</h3>
                  <p className="text-slate-600 text-sm">{t('guide_settings_logo_desc')}</p>
                  <ul className="text-sm text-slate-600 mt-2 space-y-1">
                    <li>• {t('guide_settings_logo_formats')}</li>
                    <li>• {t('guide_settings_logo_size')}</li>
                  </ul>

                  <h3 className="font-semibold text-slate-800 mt-6 mb-3">{t('guide_settings_tolerance_title')}</h3>
                  <p className="text-slate-600 text-sm">{t('guide_settings_tolerance_desc')}</p>

                  <h3 className="font-semibold text-slate-800 mt-6 mb-3">{t('guide_settings_api_title')}</h3>
                  <p className="text-slate-600 text-sm">{t('guide_settings_api_desc')}</p>
                </CardContent>
              </Card>
            </div>

            {/* FAQ */}
            <div className={`guide-section ${activeSection === 'faq' ? 'block' : 'hidden print:block'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                    {t('guide_faq_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { q: t('guide_faq_q1'), a: t('guide_faq_a1') },
                    { q: t('guide_faq_q2'), a: t('guide_faq_a2') },
                    { q: t('guide_faq_q3'), a: t('guide_faq_a3') },
                    { q: t('guide_faq_q4'), a: t('guide_faq_a4') },
                    { q: t('guide_faq_q5'), a: t('guide_faq_a5') },
                    { q: t('guide_faq_q6'), a: t('guide_faq_a6') },
                    { q: t('guide_faq_q7'), a: t('guide_faq_a7') }
                  ].map((faq, i) => (
                    <div key={i} className="border-b border-slate-100 pb-4 last:border-0">
                      <h4 className="font-medium text-slate-800 mb-2">{faq.q}</h4>
                      <p className="text-sm text-slate-600">{faq.a}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default GuidePage;
